import { Trans, useLingui } from '@lingui/react/macro';
import { Cancel, CheckCircle, Delete, WatchLater } from '@mui/icons-material';
import { Divider, IconButton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import PropTypes from 'prop-types';

import { useRestrictClinicalInteractionsForDirectors } from '@/utils/hooks';

import { VideaTypography } from '../../../common-components/elements/VideaTypography/VideaTypography';
import {
  green200,
  green400,
  orange700,
  red100,
  red600,
  white,
  yellowOrange300,
} from '../../../theme/videa-style-tokens';

const useStyles = makeStyles(() => ({
  iconRow: {
    display: 'flex',
    flexDirection: 'row',
  },
  iconCol: {
    flexDirection: 'column',
    display: 'flex',
    alignItems: 'center',
  },
  unselectedIconButton: {
    color: 'rgba(255,255,255, 0.65)',
  },
  cancelIcon: {
    color: red600,
    '&.Mui-disabled': {
      color: red100,
    },
  },
  confirmIcon: {
    color: green400,
    '&.Mui-disabled': {
      color: green200,
    },
  },
  watchIcon: {
    color: orange700,
    '&.Mui-disabled': {
      color: yellowOrange300,
    },
  },
  caption: {
    fontFamily: 'proxima-nova, sans-serif',
    fontStyle: 'normal',
    fontWeight: 'normal',
    fontSize: '10px',
    lineHeight: '12px',
    color: white,
  },
  divider: {
    width: '1px',
    height: '32px',
    alignSelf: 'center',
  },
  watchCol: {
    paddingLeft: '8px',
  },
}));

const FindingValidationSelector = (props: any) => {
  const {
    finding = {},
    isFullScreenViewer = false,
    setAnchorEls = {},
    updateFinding = () => {},
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- 'onDeleteClick' is assigned a value but never used.
    onDeleteClick = () => {},
  } = props;
  const classes = useStyles();
  const isRestrictClinicalInteractions =
    useRestrictClinicalInteractionsForDirectors();
  const { t } = useLingui();
  // eslint-disable-next-line @typescript-eslint/no-shadow -- 'finding' is already declared in the upper scope on line 69 column 5.
  const updateFindingValidation = (finding: any, option: any) => {
    updateFinding(finding, option);
  };

  const agreeRef = (el: any) => setAnchorEls.setAgreeEl(el);
  const watchRef = (el: any) => setAnchorEls.setWatchEl(el);
  const disagreeRef = (el: any) => setAnchorEls.setDisagreeEl(el);
  const rblTypes = ['cej', 'alveolar'];
  const isRBL = rblTypes.includes(finding.type);
  const monitorIconValue = isRBL ? 'Treat' : 'Monitor';
  const monitorIconDisplayText = isRBL ? t`Treat` : t`Monitor`;

  const isUserAnnotation = finding.source !== 'AI';

  return (
    <div className={classes.iconRow}>
      <>
        {!isUserAnnotation && (
          <div className={classes.iconCol}>
            <IconButton
              ref={setAnchorEls && setAnchorEls.setAgreeEl ? agreeRef : null}
              aria-label="Agree"
              data-testid="button-confirm-finding"
              color="secondary"
              disabled={isFullScreenViewer || isRestrictClinicalInteractions}
              onClick={() => updateFindingValidation(finding, 'Agree')}
              size="large"
              className={`${
                finding.validation === 'Agree'
                  ? classes.confirmIcon
                  : classes.unselectedIconButton
              } ${finding.validation === 'Agree' ? 'enabled' : 'disabled'}`}
            >
              <CheckCircle />
            </IconButton>
            <VideaTypography
              variant="labelExtrasmall"
              className={classes.caption}
            >
              <Trans>Confirm</Trans>
            </VideaTypography>
          </div>
        )}

        <div className={classes.iconCol}>
          <IconButton
            ref={
              setAnchorEls && setAnchorEls.setDisagreeEl ? disagreeRef : null
            }
            aria-label="Disagree"
            data-testid="button-reject-finding"
            color="secondary"
            disabled={isFullScreenViewer || isRestrictClinicalInteractions}
            onClick={() => {
              isUserAnnotation
                ? onDeleteClick(finding)
                : updateFindingValidation(finding, 'Disagree');
            }}
            // @ts-expect-error TS(2339): Property 'iconButton' does not exist on type 'Clas... Remove this comment to see the full error message
            className={`${classes.iconButton} ${
              finding.validation === 'Disagree'
                ? classes.cancelIcon
                : classes.unselectedIconButton
            } ${finding.validation === 'Disagree' ? 'enabled' : 'disabled'}`}
            size="large"
          >
            {!isUserAnnotation ? <Cancel /> : <Delete />}
          </IconButton>
          <VideaTypography
            variant="labelExtrasmall"
            className={classes.caption}
          >
            {isUserAnnotation ? t`Delete` : t`Reject`}
          </VideaTypography>
        </div>
      </>

      {!isUserAnnotation && (
        <>
          <Divider
            className={classes.divider}
            orientation="vertical"
            flexItem
          />
          <div className={`${classes.iconCol} ${classes.watchCol}`}>
            <IconButton
              ref={setAnchorEls && setAnchorEls.setWatchEl ? watchRef : null}
              aria-label={monitorIconDisplayText}
              data-testid="button-monitor-finding"
              color="secondary"
              disabled={isFullScreenViewer || isRestrictClinicalInteractions}
              onClick={() => {
                if (
                  finding.validation === monitorIconValue &&
                  monitorIconValue === 'Treat'
                ) {
                  updateFindingValidation(finding, ' ');
                } else {
                  updateFindingValidation(finding, monitorIconValue);
                }
              }}
              // @ts-expect-error TS(2339): Property 'iconButton' does not exist on type 'Clas... Remove this comment to see the full error message
              className={`${classes.iconButton} ${
                finding.validation === monitorIconValue
                  ? classes.watchIcon
                  : classes.unselectedIconButton
              } ${
                finding.validation === monitorIconValue ? 'enabled' : 'disabled'
              }`}
              size="large"
            >
              <WatchLater />
            </IconButton>
            <VideaTypography
              variant="labelExtrasmall"
              className={classes.caption}
            >
              {monitorIconDisplayText}
            </VideaTypography>
          </div>
        </>
      )}
    </div>
  );
};

FindingValidationSelector.propTypes = {
  finding: PropTypes.object,
  findingGroupType: PropTypes.string,
  isFullScreenViewer: PropTypes.bool,
  isUserGenerated: PropTypes.bool,
  onDeleteClick: PropTypes.func,
  setAnchorEls: PropTypes.object,
  updateFinding: PropTypes.func,
};

export default FindingValidationSelector;
